// Attorney Notepad — frontend helpers (clip → deep-link, author color).
//
// See ``plans/ATTORNEY_NOTEPAD_PLAN.md``. Clip ``anchor`` mirrors the viewer's
// citation location dispatch (char_range / bbox / time_range), so a clip
// deep-links back through the SAME ``?doc=&page=&t=&q=`` contract that
// DocumentDeepLink already consumes.

const CLIP_KIND_LABEL = Object.freeze({
  highlight: 'Highlight',
  box: 'Region',
  time: 'Moment',
  chat: 'Arc answer',
  note: 'Note',
});

const CLIP_KIND_ICON = Object.freeze({
  highlight: '🖍️',
  box: '🔲',   // region/bounding-box (⬚ read as an empty checkbox)
  time: '⏱️',
  chat: '💬',
  note: '🗒️',
});

export function clipKindLabel(kind) { return CLIP_KIND_LABEL[kind] || kind; }
export function clipKindIcon(kind) { return CLIP_KIND_ICON[kind] || '📌'; }

// Notebook visibility scope — icon + short label for tab/menu affordances.
// Mirrors the backend enum (private | shared | organization).
const VISIBILITY_META = Object.freeze({
  private: { icon: '🔒', label: 'Private', desc: 'Only you' },
  shared: { icon: '👥', label: 'Shared', desc: 'You + selected members' },
  organization: { icon: '🌐', label: 'Team', desc: 'Everyone on the case' },
});

export function visibilityMeta(visibility) {
  return VISIBILITY_META[visibility] || VISIBILITY_META.organization;
}

export function visibilityLabel(nb) {
  const meta = visibilityMeta(nb?.visibility);
  if (nb?.visibility === 'shared' && nb?.share_count) {
    return `${meta.label} (${nb.share_count})`;
  }
  return meta.label;
}

export const VISIBILITY_OPTIONS = Object.freeze(['organization', 'shared', 'private']);

// Stable, readable color per author — same hashing the drawer legend and
// (future) on-document overlays share, so an author's clips are one color
// everywhere. Palette chosen for contrast on a light surface.
const AUTHOR_PALETTE = [
  '#2563eb', '#16a34a', '#db2777', '#d97706',
  '#7c3aed', '#0891b2', '#dc2626', '#65a30d',
];

export function authorColor(authorId) {
  if (!authorId) return AUTHOR_PALETTE[0];
  let h = 0;
  for (let i = 0; i < authorId.length; i++) {
    h = (h * 31 + authorId.charCodeAt(i)) >>> 0;
  }
  return AUTHOR_PALETTE[h % AUTHOR_PALETTE.length];
}

export function authorInitials(name, authorId) {
  const src = (name || '').trim();
  if (src) {
    const parts = src.split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || src[0].toUpperCase();
  }
  return (authorId || '?').slice(0, 2).toUpperCase();
}

/**
 * Build a viewer deep-link for a clip, or null if it has no navigable source.
 *
 * - highlight/box: ?doc=<id>&page=<n>&q=<quote>  (reuses the snippet highlight
 *   path; bbox-precise overlay rendering arrives with the persistent-overlay
 *   phase).
 * - time: ?doc=<id>&t=<seconds>
 * - chat: link to the first citation's document, if any.
 * - note: not navigable (null).
 */
export function clipDeepLink(clip, pathname) {
  if (!clip) return null;
  const anchor = clip.anchor || {};

  if ((clip.kind === 'highlight' || clip.kind === 'box') && clip.document_id) {
    const params = new URLSearchParams();
    params.set('doc', clip.document_id);
    if (anchor.page != null) params.set('page', String(anchor.page));
    const snip = (clip.quote || '').trim();
    if (snip) params.set('q', snip.slice(0, 2000));
    return `${pathname}?${params.toString()}`;
  }

  if (clip.kind === 'time' && clip.document_id) {
    const params = new URLSearchParams();
    params.set('doc', clip.document_id);
    if (anchor.time_start != null) params.set('t', String(anchor.time_start));
    return `${pathname}?${params.toString()}`;
  }

  if (clip.kind === 'chat') {
    const first = Array.isArray(clip.citations) ? clip.citations.find(c => c?.document_id) : null;
    if (first?.document_id) {
      const params = new URLSearchParams();
      params.set('doc', first.document_id);
      if (first.page != null) params.set('page', String(first.page));
      if (first.timestamp != null) params.set('t', String(first.timestamp));
      const snip = (first.snippet || '').trim();
      if (snip) params.set('q', snip.slice(0, 2000));
      return `${pathname}?${params.toString()}`;
    }
    // No citation doc (e.g. a selection clipped from an answer) — link back to
    // the Ask Arc tab for this case, carrying the conversation id so the tab
    // can open the exact source conversation (not a blank chat). When the clip
    // also recorded which message it came from, pass that so the tab can scroll
    // to and highlight the exact answer.
    if (clip.conversation_id && clip.case_id) {
      const params = new URLSearchParams();
      params.set('conversation_id', clip.conversation_id);
      if (clip.message_id) params.set('message_id', String(clip.message_id));
      return `/case/${clip.case_id}/exploration?${params.toString()}`;
    }
  }

  return null;
}

// ── Export-to-memo ───────────────────────────────────────────────────────

function fmtClipTime(secs) {
  if (secs == null || !Number.isFinite(secs)) return '';
  const s = Math.floor(secs % 60);
  const m = Math.floor(secs / 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Human source label for a clip ("Police report.pdf, p.3" / "Arc answer" / …). */
export function clipSourceLabel(clip, documentsById = {}) {
  const fn = clip.document_id ? documentsById[clip.document_id]?.filename : null;
  if (clip.kind === 'highlight' || clip.kind === 'box') {
    const page = clip.anchor?.page;
    return fn ? `${fn}${page != null ? `, p.${page}` : ''}` : 'Document';
  }
  if (clip.kind === 'time') {
    const t = clip.anchor?.time_start;
    return fn ? `${fn}${t != null ? ` @ ${fmtClipTime(t)}` : ''}` : 'Media';
  }
  if (clip.kind === 'chat') return 'Arc answer';
  return 'Note';
}

/**
 * Display title for a clip. Uses the attorney's saved `title` when set;
 * otherwise derives a default from the capture type + source, e.g.
 * "Region - report.pdf, p.3" / "Moment - bodycam.mp4 @ 1:23" / "Arc answer".
 * Doc-anchored kinds get a "{Type} - {source}" prefix; chat/note labels
 * already read as titles on their own.
 */
export function clipTitle(clip, documentsById = {}) {
  if (!clip) return '';
  const custom = (clip.title || '').trim();
  if (custom) return custom;
  const src = clipSourceLabel(clip, documentsById);
  if (clip.kind === 'highlight' || clip.kind === 'box' || clip.kind === 'time') {
    return `${clipKindLabel(clip.kind)} - ${src}`;
  }
  return src;
}

function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Assemble selected clips into a memo. Returns { markdown, html } — the html
 * is escaped and used for both the in-modal preview and rich-text copy/print;
 * the markdown is for clipboard text/plain + the .md download.
 */
export function buildMemo(clips, { caseName, notebookName, dateStr, documentsById = {} } = {}) {
  const title = notebookName
    ? `${notebookName} — ${caseName || 'Untitled case'}`
    : `Case Notes — ${caseName || 'Untitled case'}`;
  const sub = `${dateStr || ''}${dateStr ? ' · ' : ''}${clips.length} clip${clips.length === 1 ? '' : 's'}`;

  const mdParts = [`# ${title}`, `_${sub}_`, ''];
  const htmlParts = [
    `<h1>${escapeHtml(title)}</h1>`,
    `<p class="memo-sub">${escapeHtml(sub)}</p>`,
  ];

  clips.forEach((clip, i) => {
    const n = i + 1;
    const heading = `${n}. ${clipTitle(clip, documentsById)}`;
    const author = clip.author_name || 'Team member';

    mdParts.push(`## ${heading}`);
    htmlParts.push(`<h2>${escapeHtml(heading)}</h2>`);

    if (clip.quote) {
      const q = clip.quote.trim();
      mdParts.push(q.split('\n').map(l => `> ${l}`).join('\n'));
      htmlParts.push(`<blockquote>${escapeHtml(q).replace(/\n/g, '<br>')}</blockquote>`);
    }
    if (clip.note) {
      mdParts.push('', clip.note.trim());
      htmlParts.push(`<p class="memo-note">${escapeHtml(clip.note.trim()).replace(/\n/g, '<br>')}</p>`);
    }
    mdParts.push('', `— ${author}`, '');
    htmlParts.push(`<p class="memo-attr">— ${escapeHtml(author)}</p>`);
  });

  return { markdown: mdParts.join('\n'), html: htmlParts.join('\n') };
}
